import { useEffect } from "react";
import { getMaxDateForLiveView, getMinDateForLiveView } from "../utils/dateFormatter";
/**
 * Custom hook to manage Live View Only date constraints on a CatDatepickerField
 * by directly manipulating the internal cat-calendar element in its shadow DOM.
 *
 * This is a workaround for the Blocks datepicker component not reactively updating
 * its internal calendar's min/max date constraints when props change.
 *
 * @param dateRef - React ref to the CatDatepickerField element
 * @param isLiveViewOnly - Whether Live View Only mode is active
 * @param expiryDate - Current expiry date value
 *
 * @example
 * ```tsx
 * const dateRef = useRef(null);
 * const [formData, setFormData] = useState({ features: { liveViewOnly: false }, expiryDate: "" });
 *
 * useDatePickerLiveViewConstraints(dateRef, formData.features.liveViewOnly, formData.expiryDate);
 *
 * return (
 *   <CatDatepickerField
 *     ref={dateRef}
 *     disabledMinDate={getMinDateForLiveView()}
 *     value={formData.expiryDate}
 *   />
 * );
 * ```
 */
export const useDatePickerLiveViewConstraints = (dateRef, isMaxTwoDays, isMaxThreeMonths, expiryDate) => {
    useEffect(() => {
        const el = dateRef.current;
        if (el && el.shadowRoot) {
            // Find the internal calendar inside the shadow DOM
            const calendar = el.shadowRoot.querySelector("cat-calendar");
            if (calendar) {
                // Manually update the calendar's internal properties
                // since the parent field isn't passing them down reactively
                calendar.disabledMinDate = getMinDateForLiveView();
                calendar.disabledMaxDate = getMaxDateForLiveView(isMaxTwoDays, isMaxThreeMonths);
                // Force the calendar to refresh its view
                if (typeof calendar.updateGrid === "function") {
                    const dateToView = expiryDate ? new Date(expiryDate) : new Date();
                    calendar.updateGrid(dateToView);
                }
            }
        }
    }, [dateRef, isMaxTwoDays, isMaxThreeMonths, expiryDate]);
};
